import type { LevelDef } from './types';

/**
 * Day 4 — Thursday. The second night he follows her. Through Riverside Park:
 * a maze of bamboo where the lawn was (lamps mark the way to the lit path;
 * the rest is dark), then a hedge garden, the dark shortcut. The keychain she lost at work is
 * waiting on a bench. Across the river, he is already waiting ahead of her.
 * Teaches: stay in the light... then don't trust it.
 */
export const night3: LevelDef = {
  id: 'night3',
  night: 4,
  title: 'Riverside Park',
  subtitle: 'Thursday · 23:20',
  look: 'casual',
  card: ['You took the long way, through the park.', 'Fewer streetlights. Fewer people.', 'Your keychain went missing at work on Tuesday.'],
  outfit: 'casual',
  clockStart: 23 * 60 + 20,
  map: [
    '###########################', // 0
    '###################H#######', // 1
    '################....#######', // 2
    '################.##########', // 3
    '################B##########', // 4
    '################.##########', // 5
    '###.....V....L..........###', // 6
    '###.#########.#########.###', // 7
    '###.#########.#########.###', // 8
    '###.#########.#########.###', // 9
    '###.#########.#########L###', // 10
    '###.#########.#########.###', // 11
    '###F#########X....L.....C##', // 12
    '###.#########.#########.###', // 13
    '###.#########.#########L###', // 14
    '###.#########.#########.###', // 15
    '###.#########.#########.###', // 16
    '###.........L........F..###', // 17
    '#########.#######L#########', // 18
    'wwwwwwwwwbwwwwwwwbwwwwwwwww', // 19
    'wwwwwwwwwbwwwwwwwbwwwwwwwww', // 20
    'wwwwwwwwwbwwwwwwwbwwwwwwwww', // 21
    '####hhhhhphhhhhhhLjj#######', // 22  hedge maze (the dark way)
    '####hppppppphppphgjj#######', // 23
    '####hphhhhhphhhphgjj#######', // 24
    '####hphppppphppphgjj#######', // 25
    '####hphphhhhhphhhgjj#######', // 26
    '####hphppppppphppgjj#######', // 27
    '####hphhhphhhhhphgjj#######', // 28
    '####hppphppppppphgjj#######', // 29
    '####hhhhhhhhhGhhhgjjjjj####', // 30
    '####jgggggjggggggLgggLj####', // 31 bamboo maze (lamps mark the way to the lit path)
    '####jgjgjgjgjjjjjjjjjgj####', // 32
    '####jgjgjgggjgggggjLggj####', // 33
    '####jjjgjgjjjjjgjjjgjjj####', // 34
    '####jgggjgjgggjgggggjgj####', // 35
    '####jgjjjgjgjnjjjgjgjgj####', // 36
    '####jgggjgggjggLgggLggj####', // 37
    '####jgjgjgjjjjjgjjjjjgj####', // 38
    '####jgjgjgjgggjgjgggjgj####', // 39
    '####jgjgjgjgjgjgjgjjjgj####', // 40
    '####jgjgggggjLgLjgggggj####', // 41
    '#############.#############', // 42
    '#############V#############', // 43
    '#############.#############', // 44
    '#############.#############', // 45
    '#############L#############', // 46
    '#############.#############', // 47
    '#############.#############', // 48
    '#############S#############', // 49
    '#############.#############', // 50
    '#############P#############', // 51
  ],
  zones: [
    { r0: 42, zone: 1 },
    { r0: 19, r1: 41, zone: 2 },
    { r1: 18, zone: 3 },
  ],
  wind: 0.55,
  battery: 28,
  pace: { tension: 0.62, patience: 700 },
  theme: {
    sky: '#060a12',
    fog: { color: '#08101a', density: 0.024 },
    hemi: { sky: '#5a76a4', ground: '#10140c', intensity: 1.05 },
    ambient: 0.6,
    moon: 1.0,
  },
  stranger: { walkMax: 2.8, chaseSpeed: 3.0, gapMax: 40, spawnDelay: 10 },
  lines: {
    intro: 'The park is quicker. Stay where it\'s bright.',
    firstSight: 'Someone behind me. He\'s been there since the park gate.',
    act3: 'Under these lights he can see exactly where I am.',
    act3b: 'Maybe the dark is safer now.',
    lawn: 'Bamboo everywhere. I can\'t see where the path goes… or who\'s on it.',
    keychain: 'That\'s… my keychain. On the bench. I lost it at work on Tuesday.',
    path: 'The hedge garden. Dark… but he can\'t see me in here.',
    pathWait: 'He stopped at the hedges.',
    pathExitGone: 'The footbridge. Did he lose me?',
    bridge: 'The bridge. Everyone can see me up here.',
    ahead: 'Someone… up ahead. Standing there. How did he get in front of me?',
    roadworks: 'Blocked again.',
    konbini: 'A konbini. People. Light.',
    homeStretch: 'Almost home. Don\'t run. Don\'t run.',
  },
  mom: {
    first: { node: 'start', at: 7 },
    script: {
      start: {
        text: 'Did you find your keys? I can leave the door unlocked.',
        options: [
          { text: 'Not yet', end: "I'll wait up." },
          { text: 'I think someone took them', next: 'took' },
        ],
      },
      took: {
        text: 'Took them?? Who would take your keys?',
        options: [
          { text: 'Forget it', end: 'Hmm. Come home safe.' },
          { text: "There's a man behind me", next: 'man' },
        ],
      },
      man: {
        text: 'What man?? Where are you right now?',
        options: [
          { text: 'The park', end: 'At night?! Get out of there.' },
          { text: 'Almost home', end: 'Good. Lock the door.' },
        ],
      },
      bridge: {
        text: 'Are you past the river yet?',
        options: [
          { text: 'Yes', end: 'Ok. Nearly there.' },
          { text: "He's waiting up ahead", next: 'ahead' },
        ],
      },
      ahead: {
        text: 'Turn around! Go another way!',
        options: [
          { text: 'Ok', end: 'Keep moving.' },
          { text: 'There is no other way', end: "...then don't stop." },
        ],
      },
    },
  },
  onEnterCell(ctx, cell, prev) {
    const key = `${cell.c},${cell.r}`;
    if (cell.r === 40 || cell.r === 41) ctx.sayOnce('lawn');
    if (key === '13,36' || key === '12,36' || key === '14,36' || key === '13,35' || key === '13,37') {
      if (!ctx.said('keychain')) {
        ctx.spike(0.08);
        ctx.sayOnce('keychain');
      }
    }
    if (key === '17,20') ctx.sayOnce('bridge');
    if (key === '13,13' && !ctx.said('roadworks')) {
      ctx.spike(0.05);
      ctx.sayOnce('roadworks');
    }
    if (key === '23,12') ctx.sayOnce('konbini');
    if (cell.r === 2) ctx.sayOnce('homeStretch');
    // Over the river: he is somehow already ahead of her, on the road she'll take.
    if (cell.r === 17 && prev.r === 18 && !ctx.said('ahead')) {
      const placed = cell.c >= 13 ? ctx.strangerAhead(23, 13) : ctx.strangerAhead(3, 10);
      if (placed) ctx.sayLater('ahead', 4);
    }
  },
  onZoneChange(ctx, zone) {
    if (zone === 2) ctx.notify('yui3', 'Yui', 'why r u walking thru the park at this hour??', 'normal', 12);
    if (zone === 3 && !ctx.said('act3')) {
      ctx.sayOnce('act3');
      ctx.sayLater('act3b', 4.5);
      ctx.momLater('bridge', 7);
    }
  },
};
